/* eslint-disable */
import { useEffect, useState } from "react";
import { Check, Copy, Upload, X } from "lucide-react";
import Swal from "sweetalert2";
import { supabase } from "../utils/supabase";
import { currentExternalId, trackPurchase, trackPurchaseBrowser } from "../utils/metaPixel";
import { getQuizPath } from "../utils/quizPath";
import { adSourceString } from "../utils/attribution";

const SUPABASE_URL = "https://omgwcmqastewylltshdu.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9tZ3djbXFhc3Rld3lsbHRzaGR1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzA0Mjc5MzIsImV4cCI6MjA0NjAwMzkzMn0.Dd82cSB_T2fKSQgBW4oQtFtAMFVXC1gkjW78FaVIoF8";

const BANK = {
  name: "Allied Bank",
  title: "Sajawal.School",
  accountNumber: "06050010046282980035",
  iban: "PK91ABPA0010046282980035",
};

// Mobile wallet option — Easypaisa / JazzCash transfers go to this number
const WALLET = {
  number: "03000132773",
  title: "Shazal Ahmed",
};

const PLANS = {
  starter: { label: "CORE", price: "6,700", amount: "6700" },
  pro: { label: "ADVANCE", price: "10,700", amount: "10700" },
};


/* ------------------------------------------------------------------ */
/* Screenshot compression                                              */
/* ------------------------------------------------------------------ */

/**
 * The OCR provider's free tier rejects uploads much over ~1 MB, answering with
 * a 502 that this page turned into "Verification Failed". Phone screenshots are
 * routinely 2-5 MB, so buyers who had genuinely paid were told their receipt
 * was invalid purely because of their camera resolution — roughly a fifth of
 * all uploads. These two limits keep every upload comfortably inside the
 * provider's ceiling while leaving receipt text easily legible to OCR.
 */
const OCR_MAX_BYTES = 900 * 1024;
const OCR_MAX_EDGE = 1800;

/** Decode a file to something drawable, preferring the fast path. */
async function loadImage(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      /* fall through to the <img> path (older iOS Safari, odd formats) */
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode failed"));
    };
    img.src = url;
  });
}

/**
 * Downscale a payment screenshot so OCR will accept it.
 *
 * Returns the ORIGINAL file if it's already small enough or if anything goes
 * wrong — an oversized upload that might fail is still better than blocking a
 * paying customer outright.
 */
async function compressScreenshot(file) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  if (file.size <= OCR_MAX_BYTES) return file;

  try {
    const source = await loadImage(file);
    const width = source.width || source.naturalWidth;
    const height = source.height || source.naturalHeight;
    if (!width || !height) return file;

    const scale = Math.min(1, OCR_MAX_EDGE / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);

    const ctx = canvas.getContext("2d");
    // Receipts saved as transparent PNGs flatten to black when encoded as
    // JPEG, which destroys the text. Paint white underneath first.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    if (typeof source.close === "function") source.close();

    let smallest = null;
    for (const quality of [0.85, 0.7, 0.55, 0.4]) {
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", quality)
      );
      if (!blob) continue;
      if (!smallest || blob.size < smallest.size) smallest = blob;
      if (blob.size <= OCR_MAX_BYTES) break;
    }

    if (smallest && smallest.size < file.size) {
      return new File([smallest], "screenshot.jpg", { type: "image/jpeg" });
    }
    return file;
  } catch {
    return file;
  }
}

function getPKT() {
  return new Date().toLocaleString("en-US", { timeZone: "Asia/Karachi" });
}

const GOOGLE_ADS_PURCHASE_SEND_TO = "AW-11080800148/coL5CMeIrNccEJSv3qMp";

/**
 * Phone in E.164 (+92XXXXXXXXXX) — the only shape Google matches on.
 *
 * Kept local on purpose. The Meta normalizer in utils/metaPixel.js produces
 * 92XXXXXXXXXX with no plus, which is what Meta wants and what Google does not;
 * the two must not be collapsed into one helper.
 */
function toE164Pk(raw) {
  let d = (raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("92")) return `+${d}`;
  if (d.startsWith("0")) return `+92${d.slice(1)}`;
  if (d.length === 10 && d.startsWith("3")) return `+92${d}`;
  return `+${d}`;
}

/**
 * Google Ads Purchase conversion, returned as a promise that settles once
 * Google acknowledges the hit (or a timeout fires).
 *
 * Two things this fixes, both specific to Google Ads:
 *
 * Enhanced conversions — gtag SHA-256s the buyer's details in the browser
 * before they leave, so no plain PII is transmitted, and Google can then match
 * a sale whose _gcl_aw cookie was blocked, cleared, or expired. That cookie is
 * JS-set, so Safari/ITP caps it at seven days, and doubleclick.net is blocked
 * far more aggressively than google-analytics.com. That is why this conversion
 * action read low against the GA4 import for the very same purchases.
 *
 * event_callback — the caller redirects to another origin right after this.
 * The conversion ping is a two-hop googleadservices.com -> doubleclick.net
 * round trip, and on a slow mobile connection a navigation can cut it off in
 * flight. Every other Google conversion on the site fires with no navigation
 * behind it, which is exactly why this was the only one recording unreliably.
 */
function sendGoogleAdsPurchase({ name, email, phone, amount, transactionId }) {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      return resolve();
    }

    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    try {
      const userData = {};
      const em = (email || "").trim().toLowerCase();
      if (em) userData.email = em;
      const ph = toE164Pk(phone);
      if (ph) userData.phone_number = ph;

      const parts = (name || "").trim().split(/\s+/).filter(Boolean);
      if (parts.length) {
        userData.address = {
          first_name: parts[0].toLowerCase(),
          ...(parts.length > 1
            ? { last_name: parts.slice(1).join(" ").toLowerCase() }
            : {}),
          country: "PK",
        };
      }
      if (Object.keys(userData).length) {
        window.gtag("set", "user_data", userData);
      }

      window.gtag("event", "conversion", {
        send_to: GOOGLE_ADS_PURCHASE_SEND_TO,
        value: Number(amount),
        currency: "PKR",
        transaction_id: transactionId,
        event_callback: done,
        event_timeout: 2000,
      });
    } catch (err) {
      console.warn("[Google Ads] purchase conversion failed", err);
      return done();
    }

    // Hard floor. A buyer never waits on Google longer than this, callback or
    // not — the redirect must happen even if gtag never answers.
    setTimeout(done, 2500);
  });
}

async function saveLeadStatus(name, email, phoneNumber, plan, status) {
  const planLabel = PLANS[plan].label;
  const row = {
    name,
    email,
    phone_number: phoneNumber,
    plan: `BT - ${planLabel === "CORE" ? "Starter" : "Pro"} - ${status}`,
    path: getQuizPath(), // tag the conversion row with the same persona
    ad_source: adSourceString(), // google / meta / organic — first-party attribution
    created_date: getPKT(),
  };
  let { error } = await supabase.from("leads").insert([row]);
  if (error) {
    // Attribution must never block recording a payment status — retry bare.
    const { ad_source, ...bareRow } = row;
    ({ error } = await supabase.from("leads").insert([bareRow]));
  }
  if (error) console.error("leads insert error:", error);
}

/**
 * Hard cap on a single verification attempt. Nothing in the chain below has
 * its own deadline — browser fetch will happily wait minutes, and the edge
 * function used to wait on the OCR provider just as indefinitely — so when the
 * provider stalled, the buyer sat on "Verifying..." forever. The existing
 * retry never rescued them: it triggers on a RECEIVED 5xx, and a hang never
 * produces a response at all.
 *
 * MUST stay above the edge function's 55s OCR budget. During the 2026-08-18
 * OCR.space slowdown the provider needed ~30s per image; a 30s cap here
 * aborted attempts the function was about to answer. 65s lets the function
 * finish its full degraded-mode window, so a slow provider means a slow
 * approval instead of a failed one.
 */
const OCR_ATTEMPT_TIMEOUT_MS = 65000;

async function runOCRVerification(imageFile, plan) {
  // Shrink before upload — see compressScreenshot. This is what stops legit
  // receipts from being rejected for being too big.
  const uploadFile = await compressScreenshot(imageFile);

  const send = async () => {
    const payload = new FormData();
    payload.append("image", uploadFile, uploadFile.name || "screenshot.jpg");
    payload.append("plan", plan);

    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), OCR_ATTEMPT_TIMEOUT_MS);
    try {
      return await fetch(`${SUPABASE_URL}/functions/v1/payment-screenshot-ocr`, {
        method: "POST",
        headers: { Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        body: payload,
        signal: controller.signal,
      });
    } finally {
      clearTimeout(deadline);
    }
  };

  // One retry on a transient upstream hiccup — a timed-out attempt now
  // included, since a provider stall usually clears on the next request. Two
  // attempts total in every path, so the worst case is bounded at roughly a
  // minute before the buyer lands in the WhatsApp-rescue dialog. A buyer who
  // has already paid should not be turned away by a single flaky request.
  let res;
  try {
    res = await send();
    if (!res.ok && res.status >= 500) {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      res = await send();
    }
  } catch (firstErr) {
    await new Promise((resolve) => setTimeout(resolve, 1200));
    res = await send(); // a second failure propagates to the Error dialog
  }

  if (!res.ok) throw new Error(`OCR function returned ${res.status}`);
  return res.json();
}

export default function BankTransferDetails() {
  const [copiedField, setCopiedField] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  // Defaults to CORE so the price on this page matches the Rs 6,700 promised by
  // the ads and the landing page. It previously defaulted to ADVANCE, so buyers
  // who clicked a 6,700 CTA met an 10,700 total at the payment step — and anyone
  // who paid 6,700 without switching was recorded, and reported to Meta, as an
  // 10,700 purchase. ADVANCE is now an explicit upgrade rather than a default.
  const [selectedPlan, setSelectedPlan] = useState("starter");

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      Swal.fire({ title: "Invalid File", text: "Please upload an image (PNG, JPG, JPEG)", icon: "warning" });
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removeFile = () => {
    setSelectedFile(null);
    setImagePreview(null);
  };

  const handleVerify = async () => {
    if (!selectedFile) {
      Swal.fire({ title: "No Screenshot", text: "Please upload your payment screenshot first", icon: "warning" });
      return;
    }

    const name = localStorage.getItem("userName");
    const email = localStorage.getItem("userEmail");
    const phoneNumber = localStorage.getItem("phoneNumber");

    setIsVerifying(true);

    try {
      const result = await runOCRVerification(selectedFile, selectedPlan);

      if (!result.success) throw new Error(result.error || "OCR failed");

      if (result.approved) {
        // Deterministic event id: same buyer + plan + day always produces the
        // same id, so retries, refreshes, and the thank-you page's browser
        // Purchase all deduplicate into one purchase at Meta.
        const amount = PLANS[selectedPlan].amount;
        const phoneDigits = (phoneNumber || "").replace(/\D/g, "");
        const purchaseEventId = `pur_${phoneDigits || "anon"}_${amount}_${new Date().toISOString().slice(0, 10)}`;

        // Browser pixel Purchase fires here, at the same instant as the CAPI
        // event below and with the same eventID, so Meta merges them into one
        // sale. Firing it here rather than only on the thank-you page means it
        // no longer depends on the buyer surviving the modal and the redirect,
        // and it can carry advanced matching because the customer's details
        // exist on this page.
        trackPurchaseBrowser({ name, email, phone: phoneNumber, amount, eventId: purchaseEventId });

        // Server-side Purchase with hashed customer data (best match quality).
        await trackPurchase({ name, email, phone: phoneNumber, amount, eventId: purchaseEventId });

        /**
         * Google Ads purchase conversion + GA4 purchase, fired HERE rather than
         * only on the thank-you page.
         *
         * Both previously lived exclusively on sajawal.school/thank-you, which a
         * buyer only reaches after a 2.5s modal, a cross-origin redirect and a
         * 308 to www. Every Google conversion that fires in place on this page
         * (begin_checkout, generate_lead) records fine, while the purchase — the
         * only one behind that redirect — did not. It's the same failure the
         * Meta Purchase already had, and the same fix: fire at the moment of
         * approval, where the buyer definitely still is.
         *
         * transaction_id is the deterministic purchase id, identical to the one
         * the thank-you page sends, so Google Ads and GA4 both collapse the pair
         * into one sale rather than double-counting. The thank-you copy stays as
         * a backstop for anyone whose browser drops these requests.
         */
        // Fired first so its ping has the whole approval modal to complete in.
        // Awaited just before the redirect below, never here — nothing about
        // the buyer's experience should wait on Google.
        const adsConversionSent = sendGoogleAdsPurchase({
          name,
          email,
          phone: phoneNumber,
          amount,
          transactionId: purchaseEventId,
        });

        if (typeof window !== "undefined" && window.gtag) {
          window.gtag("event", "purchase", {
            send_to: "G-EH4NE70PHR",
            transaction_id: purchaseEventId,
            value: Number(amount),
            currency: "PKR",
            path: getQuizPath() || "direct",
          });
        }

        if (name && email && phoneNumber) {
          await saveLeadStatus(name, email, phoneNumber, selectedPlan, "Approved");
        }

        await Swal.fire({
          title: "Payment Verified!",
          text: "Your screenshot has been approved. Redirecting you now...",
          icon: "success",
          timer: 2500,
          showConfirmButton: false,
        });

        // xid rides along so the thank-you page's backstop Purchase resolves to
        // the SAME external_id as this one. localStorage is per-origin, so a
        // buyer who came straight to checkout has an identity sajawal.school
        // has never seen — without this hand-off that browser event reaches
        // Meta with nothing to match on.
        // Already settled in practice — the modal above runs 2.5s and the
        // conversion has a 2.5s hard floor — so this adds no delay for the
        // buyer. It only guarantees the ping is not cut off by the navigation.
        await adsConversionSent;

        const xid = currentExternalId();
        const xidParam = xid ? `&xid=${encodeURIComponent(xid)}` : "";
        window.location.href = `https://sajawal.school/thank-you?amount=${amount}&eid=${encodeURIComponent(purchaseEventId)}${xidParam}`;
      } else {
        if (name && email && phoneNumber) {
          await saveLeadStatus(name, email, phoneNumber, selectedPlan, "Rejected");
        }

        const swalResult = await Swal.fire({
          title: "Verification Failed",
          html: "We couldn't verify your payment screenshot.<br><br>Please make sure the screenshot shows the transfer to the correct account and the correct amount.",
          icon: "error",
          confirmButtonText: "Contact WhatsApp Support",
          confirmButtonColor: "#25D366",
          showCancelButton: true,
          cancelButtonText: "Try Again",
        });

        if (swalResult.isConfirmed) {
          window.open("https://wa.me/923290932008", "_blank");
        }
      }
    } catch (err) {
      console.error("Verification error:", err);

      // Logged as "Error", NOT "Rejected". A crash on our side is not the same
      // event as a receipt we read and couldn't match, and lumping the two
      // together made an infrastructure failure look like a fraud signal in the
      // leads table. These rows are the ones worth chasing manually — the buyer
      // has very likely paid.
      if (name && email && phoneNumber) {
        await saveLeadStatus(name, email, phoneNumber, selectedPlan, "Error");
      }

      Swal.fire({
        title: "We Couldn't Check It Automatically",
        html: "Your payment is safe — this is a problem on our side, not with your transfer.<br><br>Send your screenshot on WhatsApp and we'll confirm your access there, usually within a couple of minutes.",
        icon: "warning",
        confirmButtonText: "Send on WhatsApp",
        confirmButtonColor: "#25D366",
        showCancelButton: true,
        cancelButtonText: "Try Again",
      }).then((r) => {
        if (r.isConfirmed) window.open("https://wa.me/923290932008", "_blank");
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-3 sm:px-4">
      {/* Refund Guarantee — visible before payment */}
      <div className="max-w-4xl mx-auto mb-5 sm:mb-6">
        <div className="flex items-start sm:items-center justify-center gap-2.5 sm:gap-3 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl px-4 py-3 sm:py-3.5 shadow-sm">
          <svg className="w-6 h-6 sm:w-7 sm:h-7 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <p className="font-poppins text-xs sm:text-sm text-gray-800 leading-relaxed">
            <span className="font-bold text-green-700">Money Back Guarantee.</span>{" "}
            If you don't get value, send one WhatsApp message. Full refund. No forms. No questions.
          </p>
        </div>
      </div>

      {/* Plan Selection */}
      <div className="mb-6 sm:mb-8">
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
          {/* Core Plan */}
          <div
            onClick={() => setSelectedPlan("starter")}
            className={`relative bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 cursor-pointer transition-all ${
              selectedPlan === "starter"
                ? "border-orange-500 shadow-xl scale-[1.02] sm:scale-105"
                : "border-gray-200 hover:border-gray-300 shadow-md"
            }`}
          >
            <div className="text-center mb-4 sm:mb-6">
              <div className="bg-gradient-to-r from-blue-100 to-blue-50 rounded-lg py-2 px-3 mb-3 sm:mb-4">
                <h3 className="font-oswald text-xl sm:text-2xl font-bold text-blue-900 tracking-wide">CORE</h3>
              </div>
              <div className="mb-3 sm:mb-4">
                <div className="font-oswald text-3xl sm:text-5xl font-extrabold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  PKR 6,700
                </div>
                <p className="font-poppins text-xs sm:text-sm text-gray-500 mt-2">One Time</p>
              </div>
            </div>
            <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
              {["Access to all core modules", "Exclusive Community Access", "Sajawal's Strategy Call Recordings", "VIP Templates & Tools"].map((f) => (
                <div key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="font-poppins text-xs sm:text-sm text-gray-700">{f}</span>
                </div>
              ))}
            </div>
            <button
              className={`w-full py-2.5 sm:py-3 px-4 rounded-lg sm:rounded-xl font-poppins text-sm sm:text-base font-semibold transition-all ${
                selectedPlan === "starter"
                  ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {selectedPlan === "starter" ? "Selected" : "Get Core Access"}
            </button>
          </div>

          {/* Advance Plan */}
          <div
            onClick={() => setSelectedPlan("pro")}
            className={`relative bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 cursor-pointer transition-all ${
              selectedPlan === "pro"
                ? "border-orange-500 shadow-xl scale-[1.02] sm:scale-105"
                : "border-gray-200 hover:border-gray-300 shadow-md"
            }`}
          >
            <div className="absolute -top-2 sm:-top-3 left-1/2 transform -translate-x-1/2">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 sm:px-4 py-0.5 sm:py-1 rounded-full shadow-lg">
                <span className="font-poppins text-[10px] sm:text-xs font-bold uppercase tracking-wide flex items-center gap-1">
                  <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  {/* "Most Popular" was a factual popularity claim the leads
                      table contradicted — CORE outsells ADVANCE. "Most
                      Valuable" is a value judgement, which is defensible, and
                      it argues the upgrade harder anyway. */}
                  Most Valuable
                </span>
              </div>
            </div>
            <div className="text-center mb-4 sm:mb-6 mt-2">
              <div className="bg-gradient-to-r from-orange-100 to-orange-50 rounded-lg py-2 px-3 mb-3 sm:mb-4">
                <h3 className="font-oswald text-xl sm:text-2xl font-bold text-orange-900 tracking-wide">ADVANCE</h3>
              </div>

              {/*
                Replaces the old countdown. No deadline, no scarcity — the pull
                is the VALUE GAP, which is true every day of the week and can't
                expire or be disproved.

                Framing is deliberate: the eye lands on "+Rs 4,000", not on
                "10,700". Once CORE is the default selection, the buyer is not
                deciding between two prices any more — they're deciding whether
                one small top-up is worth six more things. That is a much easier
                yes, and it's an honest one.
              */}
              <div className="upgrade-pull mb-3 rounded-xl border border-orange-300 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 px-3 py-2.5">
                <div className="font-poppins text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-orange-600">
                  Upgrade for only
                </div>
                <div className="font-oswald text-2xl sm:text-3xl font-bold text-orange-700 leading-tight">
                  +Rs 4,000
                </div>
                <div className="font-poppins text-[11px] sm:text-xs font-semibold text-gray-700 leading-snug mt-0.5">
                  Six more things, including live calls
                </div>
              </div>

              <div className="mb-3 sm:mb-4">
                <div className="font-oswald text-3xl sm:text-5xl font-extrabold bg-gradient-to-r from-orange-600 to-orange-800 bg-clip-text text-transparent">
                  PKR 10,700
                </div>
                <p className="font-poppins text-xs sm:text-sm text-gray-500 mt-2">One Time. Lifetime.</p>
              </div>
            </div>
            <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
              {[
                "Everything in Core PLUS",
                "All Advanced Trainings, A to Z",
                "Monthly Live Call, Strategy & Q/A with Sajawal",
                "Ecommerce Case Study",
                "AI Marketing Live Sessions",
                "Exclusive Guest Sessions with industry leaders",
                "Hiring & Referral Opportunities",
              ].map((f, i) => (
                <div key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className={`font-poppins text-xs sm:text-sm text-gray-700 ${i === 0 ? "font-semibold" : ""}`}>{f}</span>
                </div>
              ))}
            </div>
            <button
              className={`w-full py-2.5 sm:py-3 px-4 rounded-lg sm:rounded-xl font-poppins text-sm sm:text-base font-semibold transition-all ${
                selectedPlan === "pro"
                  ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {selectedPlan === "pro" ? "Selected" : "Upgrade to Advance (+Rs 4,000)"}
            </button>
          </div>
        </div>
      </div>

      {/* Bank Transfer Card */}
      <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg border border-gray-200 max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-6">
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-r from-[#ff6b35] to-[#ff8c42] rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-2 sm:mb-3 shadow-md">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm:w-7 sm:h-7">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-2.5 sm:p-3 mb-2 sm:mb-3 border border-green-200">
            <p className="font-poppins text-xs sm:text-sm text-center text-gray-700 leading-relaxed">
              Pay by <span className="font-semibold">bank transfer</span> or <span className="font-semibold">Easypaisa / JazzCash</span> — use whichever is easiest for you.
            </p>
          </div>

          <div className="mb-2 sm:mb-3 py-2">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="font-poppins text-xs text-gray-500">Amount:</span>
              <span className="relative inline-block font-poppins text-sm sm:text-base text-gray-400">
                RS 45,000
                <svg className="absolute left-0 top-1/2 w-full h-0.5" style={{ transform: "translateY(-50%) rotate(-8deg)" }} viewBox="0 0 100 2" preserveAspectRatio="none">
                  <line x1="0" y1="1" x2="100" y2="1" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </span>
            </div>
            <div className="font-oswald text-3xl sm:text-4xl font-bold text-gray-900">
              RS {PLANS[selectedPlan].price}
            </div>
            <p className="font-poppins text-xs sm:text-sm text-orange-600 font-semibold mt-1 sm:mt-2">
              {PLANS[selectedPlan].label} Plan Selected
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-2 sm:p-3 text-center">
            <p className="font-poppins text-xs sm:text-sm text-gray-600">
              <span className="font-semibold text-blue-600">Allied Bank</span> • Sajawal.School
            </p>
          </div>
        </div>

        {/* Copyable Fields */}
        <div className="space-y-2.5 sm:space-y-3 mb-4 sm:mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-sky-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-200">
            <label className="font-poppins text-[10px] sm:text-xs font-medium text-blue-700 uppercase tracking-wide mb-1.5 sm:mb-2 block">
              Account Number
            </label>
            <div className="flex items-center justify-between gap-2 sm:gap-3">
              <p className="font-mono text-sm sm:text-lg font-bold text-gray-900 tracking-wide break-all">
                {BANK.accountNumber}
              </p>
              <button
                onClick={() => copyToClipboard(BANK.accountNumber, "accountNumber")}
                className="flex-shrink-0 p-2 sm:p-2.5 bg-white rounded-lg hover:bg-blue-50 transition-all shadow-sm border border-blue-200 hover:border-blue-300"
                aria-label="Copy account number"
              >
                {copiedField === "accountNumber" ? (
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                )}
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-teal-200">
            <label className="font-poppins text-[10px] sm:text-xs font-medium text-teal-700 uppercase tracking-wide mb-1.5 sm:mb-2 block">
              IBAN Number
            </label>
            <div className="flex items-center justify-between gap-2 sm:gap-3">
              <p className="font-mono text-sm sm:text-lg font-bold text-gray-900 tracking-wide break-all">
                {BANK.iban}
              </p>
              <button
                onClick={() => copyToClipboard(BANK.iban, "iban")}
                className="flex-shrink-0 p-2 sm:p-2.5 bg-white rounded-lg hover:bg-teal-50 transition-all shadow-sm border border-teal-200 hover:border-teal-300"
                aria-label="Copy IBAN"
              >
                {copiedField === "iban" ? (
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
                )}
              </button>
            </div>
          </div>

          {/* OR divider */}
          <div className="flex items-center gap-3 py-0.5">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="font-poppins text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wide">
              or pay via mobile wallet
            </span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Easypaisa / JazzCash */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-green-200">
            <label className="font-poppins text-[10px] sm:text-xs font-medium text-green-700 uppercase tracking-wide mb-1.5 sm:mb-2 block">
              Easypaisa / JazzCash Number
            </label>
            <div className="flex items-center justify-between gap-2 sm:gap-3">
              <p className="font-mono text-sm sm:text-lg font-bold text-gray-900 tracking-wide break-all">
                {WALLET.number}
              </p>
              <button
                onClick={() => copyToClipboard(WALLET.number, "wallet")}
                className="flex-shrink-0 p-2 sm:p-2.5 bg-white rounded-lg hover:bg-green-50 transition-all shadow-sm border border-green-200 hover:border-green-300"
                aria-label="Copy Easypaisa / JazzCash number"
              >
                {copiedField === "wallet" ? (
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                )}
              </button>
            </div>
            <p className="font-poppins text-[10px] sm:text-xs text-gray-500 mt-1.5">
              Account title: <span className="font-semibold text-gray-700">{WALLET.title}</span>
            </p>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-orange-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-orange-200 mb-4 sm:mb-5">
          <p className="font-poppins text-xs sm:text-sm text-gray-700 leading-relaxed">
            After transferring, upload your payment screenshot below.{" "}
            <span className="font-semibold text-orange-700">You'll receive access right away.</span>
          </p>
        </div>

        {/* File Upload */}
        <div className="mb-4 sm:mb-5">
          {!imagePreview ? (
            <label htmlFor="screenshot-upload" className="block cursor-pointer">
              <input
                id="screenshot-upload"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div className="border-2 border-dashed border-gray-300 rounded-lg sm:rounded-xl p-6 sm:p-8 text-center hover:border-orange-400 hover:bg-orange-50 transition-all">
                <Upload className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 text-gray-400" />
                <p className="font-poppins text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Upload Payment Screenshot
                </p>
                <p className="font-poppins text-[11px] sm:text-xs text-gray-500">
                  Click to select your payment receipt
                </p>
              </div>
            </label>
          ) : (
            <div className="relative border-2 border-green-300 rounded-lg sm:rounded-xl p-3 sm:p-4 bg-green-50">
              <button
                onClick={removeFile}
                className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-red-50 transition-all"
                aria-label="Remove screenshot"
              >
                <X className="w-4 h-4 text-red-500" />
              </button>
              <img
                src={imagePreview}
                alt="Payment screenshot preview"
                className="w-full h-40 sm:h-48 object-contain rounded-lg mb-2 sm:mb-3"
              />
              <p className="font-poppins text-xs text-center text-gray-600">
                Screenshot ready for verification
              </p>
            </div>
          )}
        </div>

        {/* Verify Button */}
        {selectedFile && (
          <div className="mb-4 sm:mb-5">
            <button
              onClick={handleVerify}
              disabled={isVerifying}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-poppins text-sm sm:text-base font-semibold px-5 sm:px-6 py-3 sm:py-3.5 rounded-lg sm:rounded-xl transition-all shadow-md hover:shadow-lg w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isVerifying ? (
                <>
                  <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Verifying...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                  Verify & Get Instant Access
                </>
              )}
            </button>
          </div>
        )}

        {/* Support */}
        <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200 text-center">
          <p className="font-poppins text-xs sm:text-sm text-gray-600 mb-2">Need help with your payment?</p>
          <a
            href="https://wa.me/923290932008"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-poppins font-medium px-4 py-2 rounded-lg transition-all text-xs sm:text-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="sm:w-4 sm:h-4">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}
